/**
 * @file index.tsx
 *
 * @fileoverview Loads all organizations of the current user and puts it into a
 * global context so that it can be used anywhere inside any page.
 */
import { useQuery } from '@apollo/client';
import { startTransition, useContext, useEffect } from 'react';
import GET_UPDATABLE_PROFILES from '~/jobiqo/components/Organization/queries/updatableOrganizationProfiles.gql';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { isRecruiter } from '~/jobiqo/lib/auth';

export interface CurrentOrganizationProps {}

export const CurrentOrganization = ({}: CurrentOrganizationProps) => {
  const {
    user,
    setCurrentOrganizationProfile,
    setUpdatableOrganizationProfiles,
  }: UserContextInterface = useContext(UserContext);

  // Bail out if user is not a recruiter.
  if (!(user && isRecruiter(user))) {
    return null;
  }

  const { data } = useQuery(GET_UPDATABLE_PROFILES, {
    skip: !isRecruiter(user),
  });

  useEffect(() => {
    if (data) {
      startTransition(() => {
        const currentOrganizationId = user?.activeOrganization?.id;

        // Find org profile based on the local storage org profile id.
        const cookieOrgProfile = data?.updatableOrganizationProfiles.find(
          (orgProfile) => orgProfile?.organization?.id == currentOrganizationId,
        );

        // If there is no org profile from the storage, set the first one received from the API.
        setCurrentOrganizationProfile?.(
          cookieOrgProfile ?? data?.updatableOrganizationProfiles[0],
        );

        // Set all possible profiles.
        if (setUpdatableOrganizationProfiles) {
          setUpdatableOrganizationProfiles(data?.updatableOrganizationProfiles);
        }
      });
    }
  }, [
    data,
    user,
    setCurrentOrganizationProfile,
    setUpdatableOrganizationProfiles,
  ]);

  return null;
};
export default CurrentOrganization;
